/**
 * JOSECO-22 — sticky sub-collection bar behaviour.
 *
 * Loaded by sections/category-bar.liquid (the menu-driven bar in the header group, present on
 * every page) and by sections/multi-column.liquid when sticky_below_header is on (the
 * per-template bar it replaces). Both emit the same .subcollection-bar wrapper.
 *
 * Two jobs:
 *
 * 1. Retract the bar while scrolling down, bring it back while scrolling up — but only once
 *    the bar is actually parked under the header. The header itself never moves (the theme's
 *    "hide header on scroll" stays off), so the parking line is a constant and the bar has
 *    exactly one thing to do: slide up by its own height and come back. Direction is detected
 *    here rather than read off the theme's --header-is-visible, because with a fixed header
 *    the theme never publishes that variable.
 *    The result is published as --subcollection-bar-is-visible (1/0) on <html>, mirroring the
 *    theme's own --header-is-visible convention. custom-sticky-subnav.css does the rest,
 *    including pulling --sticky-area-height back so the collection toolbar slides up into the
 *    freed space instead of leaving a gap.
 *
 * 2. Bring the current sub-collection into view inside the horizontal scroller. The items are
 *    sized to their own label (custom-sticky-subnav.css), so ~20 sub-collections overflow at
 *    every breakpoint — measured 1710px of strip on 375px and 1670px on 1440px — and the
 *    active one is off to the right on desktop as well as on mobile.
 */
(() => {
  /* The <script> tag lives inside the section, so the theme editor runs this file again on every
     section reload — and both sections that carry a bar load it, so a page holding the old and
     the new one at once runs it twice on a cold load too. The listeners below sit on
     window/document and outlive the section, and a spent IIFE cannot take its own listeners off
     — so the first run claims them for the document and later runs bail out here. Nothing is
     lost: the shopify:section:load handler installed by the first run re-queries the selector,
     so it always drives whichever section instance is currently in the DOM. */
  if (window.__subcollectionBarInit) return;
  window.__subcollectionBarInit = true;

  const root = document.documentElement;
  /* Matched on the bar rather than on the section type: sections/category-bar.liquid (header
     group, every page) and sections/multi-column.liquid with sticky_below_header on (the
     per-template bar it replaces) both emit it. If both are ever in the page at once,
     querySelector takes the first in document order — the header-group one, since it precedes
     <main> — which is the one the header parks. */
  const SECTION_SELECTOR = '.shopify-section:has(.subcollection-bar)';

  /* Travel required before the bar reacts, measured from the last change of direction. Keeps
     micro-scrolls, trackpad jitter and iOS rubber-band bounce from flickering the bar. */
  const HIDE_AFTER = 24;
  const SHOW_AFTER = 12;

  let section = null;
  let isVisible = true;
  let wasParked = false;
  let lastY = 0;
  let anchorY = 0;
  let direction = 0;
  let queued = false;

  const setVisible = (visible) => {
    if (visible === isVisible) return;
    isVisible = visible;
    root.style.setProperty('--subcollection-bar-is-visible', visible ? '1' : '0');
  };

  /* No bar in the DOM — the customer switched sticky_below_header off, or the section is
     mid-swap. The variable has to go with it: left at 0 it keeps shrinking --sticky-area-height
     for a bar that no longer exists and the Sort by / Filter toolbar parks too high. */
  const releaseVariable = () => {
    isVisible = true;
    root.style.removeProperty('--subcollection-bar-is-visible');
  };

  /* Geometry that does not move while the page scrolls, read once instead of once a frame.
     update() runs on every animation frame of a scroll, and root.scrollHeight forces a layout
     of the whole document while getComputedStyle(section).top forces a style recalc; on a long
     collection page under a throttled CPU those two dominated the frame. Both only change on
     resize or when the section is swapped, so they are cached and invalidated there. The bar's
     own rect still has to be read per frame — that is the one value that genuinely moves. */
  let maxScroll = 0;
  let parkLine = 0;

  const measure = () => {
    maxScroll = Math.max(0, root.scrollHeight - window.innerHeight);
    if (!section) return;
    const line = parseFloat(getComputedStyle(section).top);
    parkLine = Number.isNaN(line) ? 0 : line;
  };

  const clampedScrollY = () => {
    const y = Math.max(window.scrollY, 0);
    /* Lazy-loaded products and pagination grow the page after measure() ran. Re-measuring only
       once the cached ceiling is actually exceeded keeps the common frame free of layout reads
       and still self-corrects, and the clamp only exists to swallow overscroll bounce, so being
       a few pixels stale between growth events changes no decision. */
    if (y > maxScroll) measure();
    return Math.min(y, maxScroll);
  };

  /* Parked = the sticky offset has taken over. While retracted the bar sits a full height
     above the line, so this stays true and the bar cannot pop back mid-scroll. */
  const isParked = () => section.getBoundingClientRect().top <= parkLine + 1;

  const update = () => {
    queued = false;

    if (!section || !section.isConnected) {
      section = document.querySelector(SECTION_SELECTOR);
      if (!section) {
        releaseVariable();
        return;
      }
    }

    const y = clampedScrollY();

    if (y > lastY && direction !== 1) {
      direction = 1;
      anchorY = lastY;
    } else if (y < lastY && direction !== -1) {
      direction = -1;
      anchorY = lastY;
    }

    lastY = y;

    const parked = isParked();

    /* Restart the count the moment the bar parks, so it visibly docks under the header first
       and only retracts on the next stretch of downward scrolling. */
    if (parked !== wasParked) {
      wasParked = parked;
      anchorY = y;
    }

    if (!parked) {
      setVisible(true);
      return;
    }

    if (direction === 1 && y - anchorY >= HIDE_AFTER) {
      setVisible(false);
    } else if (direction === -1 && anchorY - y >= SHOW_AFTER) {
      setVisible(true);
    }
  };

  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };

  /* The scrollLeft this file last wrote, per scroller. Read back after the write, not taken
     from the target, because the browser clamps the target at scrollWidth - clientWidth
     whenever the active item sits near the end of the list. */
  const centeredAt = new WeakMap();

  const centerCurrentItem = (onlyIfUntouched) => {
    document.querySelectorAll('.subcollection-bar multi-column').forEach((scroller) => {
      if (scroller.scrollWidth <= scroller.clientWidth) return;
      if (onlyIfUntouched && scroller.scrollLeft !== (centeredAt.get(scroller) ?? 0)) return;

      const currentLink = scroller.querySelector('[aria-current="page"]');
      if (!currentLink) return;

      const item = currentLink.closest('.multi-column__item') || currentLink;
      const scrollerBox = scroller.getBoundingClientRect();
      const itemBox = item.getBoundingClientRect();
      const offset = scroller.scrollLeft + (itemBox.left - scrollerBox.left);
      const target = offset - (scroller.clientWidth - itemBox.width) / 2;

      /* scrollLeft is written directly (never scrollIntoView) so the page cannot jump
         vertically. */
      scroller.scrollLeft = Math.max(0, target);
      centeredAt.set(scroller, scroller.scrollLeft);
    });
  };

  const start = () => {
    section = document.querySelector(SECTION_SELECTOR);

    if (!section) {
      releaseVariable();
      return;
    }

    /* A fresh section always starts un-parked, but the variable can still read 0 from the
       instance that was just replaced, which would show the customer a hidden bar for no
       reason. Publish the visible state outright — setVisible() would no-op here, since
       isVisible has never left true. */
    isVisible = true;
    wasParked = false;
    direction = 0;
    root.style.setProperty('--subcollection-bar-is-visible', '1');

    measure();
    centerCurrentItem();

    /* The tracks are max-content now, so the strip is laid out in whatever font is available
       when the line above runs — on a cold load that is the fallback, because this fires at
       DOMContentLoaded and the theme's fonts are font-display: fallback (100ms block, ~3s
       swap: snippets/css-variables.liquid:16-34). Measured on 20 labels at 375px with the
       font arriving mid-swap-window: the strip widens 1612px -> 1711px and the item this
       function had just centred slides 84px, out to the right edge — and the drift grows with
       the item's position in the list. So re-centre once the real metrics exist. Guarded on
       the scrollLeft we wrote, so a customer who has already flicked the strip in that window
       keeps their position. */
    if (document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready.then(() => centerCurrentItem(true));
    }

    lastY = anchorY = clampedScrollY();
    update();
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      /* Resize is the one event that moves both cached values: the parking line follows the
         header's height at the new width, and the document reflows. */
      measure();
      onScroll();
    },
    { passive: true }
  );

  /* Keyboard users never scroll, so tabbing into a retracted bar has to reveal it. */
  document.addEventListener('focusin', (event) => {
    if (section && section.contains(event.target)) setVisible(true);
  });

  document.addEventListener('shopify:section:load', start);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
